/**
 * `W5-T15` §4.2 — the Payment Element in our colours, from our tokens.
 *
 * The Element lives in Stripe's iframe, so our stylesheet cannot reach it: Stripe takes
 * `appearance.variables` as plain CSS values instead. A token may be `light-dark(…)` or another
 * `var()`, which Stripe cannot read, so each one is resolved by the browser on a hidden probe and the
 * **computed** value is passed on. That keeps `features/checkout` free of colour literals (AC 28): the
 * tokens stay the one place a colour is chosen. A token that does not resolve is left out, and Stripe
 * uses its own default for it.
 */
import type { Appearance } from '@stripe/stripe-js';

type Variables = NonNullable<Appearance['variables']>;

/** Which CSS property resolves each token, and the Stripe variable it becomes. */
const TOKENS: readonly (readonly [keyof Variables, string, string])[] = [
  ['colorPrimary', 'color', '--mp-color-primary'],
  ['colorBackground', 'color', '--mp-color-surface'],
  ['colorText', 'color', '--mp-color-text'],
  ['colorDanger', 'color', '--mp-color-danger'],
  ['fontFamily', 'font-family', '--mp-font-family'],
  ['borderRadius', 'border-radius', '--mp-field-radius'],
];

export function tokenAppearance(): Appearance {
  const probe = document.createElement('span');
  probe.hidden = true;
  document.body.append(probe);
  try {
    const variables: Variables = {};
    for (const [variable, property, token] of TOKENS) {
      probe.style.cssText = `${property}: var(${token})`;
      const value = getComputedStyle(probe).getPropertyValue(property).trim();
      if (value !== '' && !value.includes('var(')) variables[variable] = value;
    }
    return { theme: 'stripe', variables };
  } finally {
    probe.remove();
  }
}
